import React, {useMemo} from 'react'
import {
  ACCESS_AND_PERMISSIONS_LABELS,
  If,
  RegionNames,
  RPCDocs,
  useCurrentUser,
  useLocationsAndGroups,
} from '@therms/react-modules'
import {Icon, Label} from '@therms/atalaya'
import {MdAdminPanelSettings, MdGppBad} from 'react-icons/md'
import {FaCheck} from 'react-icons/fa6'

const {Roles} = RPCDocs.Users.GetUserAccessAndPermissions.v1

function PermissionsIndex() {
  const {anp, superAdmin} = useCurrentUser()

  const {data: locationAndGroupsData} = useLocationsAndGroups(
    anp.locations.locationAndGroupIds && {
      idFilter: anp.locations.locationAndGroupIds,
    },
  )

  const roleVisibility = useMemo(() => {
    const {roles} = anp

    if (roles)
      return {
        showAdmin: anp.roles.includes(Roles.Admin),
        showOPS: anp.roles.includes(Roles.Ops),
        showPortal: anp.roles.includes(Roles.Portal),
      }

    return {}
  }, [anp])

  if (superAdmin) {
    return (
      <div className="mt-xxl px-base flex flex-col items-center">
        <Icon Icon={MdAdminPanelSettings} color="caution" size="xxl" />

        <span className="text-color-caution text-lg">
          You have Super Admin privileges
        </span>

        <span>No permission restrictions apply to you.</span>
      </div>
    )
  }

  if (!anp)
    return (
      <div className="mt-xxl px-base flex flex-col items-center">
        <Icon Icon={MdGppBad} size="xxl" color="subtle" />
        <div className="mt-base text-color-subtle text-center text-lg">
          No Access or Permissions <br /> to display.
        </div>
      </div>
    )

  return (
    <div className="mt-xs px-base">
      <div className="gap-x-xs gap-y-base grid grid-cols-1 tracking-wide md:grid-cols-2">
        {anp.regions && (
          <div className="flex flex-col">
            <Label>Regions</Label>

            <div>
              {anp.regions.all ? (
                'All Regions'
              ) : (
                <RegionNames regionIds={anp.regions.regionIds} />
              )}
            </div>
          </div>
        )}

        {!!anp.locations &&
          (anp.locations.all ? (
            <div className="flex flex-col">
              <Label>Locations</Label>
              <div>All Locations</div>
            </div>
          ) : (
            <>
              <div className="flex flex-col">
                <Label>Locations</Label>

                {locationAndGroupsData && (
                  <div>
                    {locationAndGroupsData.locationsAndGroups
                      .filter(({type}) => type === 'location')
                      .map((location) => location.label)
                      .join(', ')}
                  </div>
                )}
              </div>

              <div className="flex flex-col">
                <Label>Location Groups</Label>

                {locationAndGroupsData && (
                  <div>
                    {locationAndGroupsData.locationsAndGroups
                      .filter(({type}) => type === 'location-group')
                      .map((group) => group.label)
                      .join(', ')}
                  </div>
                )}
              </div>
            </>
          ))}
      </div>

      <If truthy={Object.values(roleVisibility).filter((x) => !!x).length}>
        <div className="mt-xl font-bold tracking-wide uppercase">
          <Label>Apps</Label>
        </div>

        <div className="mt-xs border-border border-t" />

        <div className="gap-y-xl mt-sm flex flex-col tracking-wide">
          {roleVisibility.showAdmin && (
            <div className="flex flex-col">
              <Label>Admin</Label>

              <div className="mt-xs">
                <div className="text-color-info font-light">Permissions</div>

                <div className="mt-xxs gap-x-sm grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
                  {Object.keys(
                    ACCESS_AND_PERMISSIONS_LABELS.ADMIN_PERMISSIONS,
                  ).map((key) => {
                    const checked = anp.role.admin.permissions[key]

                    return (
                      <div
                        className={checked ? '' : `text-color-neutral-faded`}
                      >
                        <Icon
                          Icon={checked ? FaCheck : undefined}
                          className="mr-xxs"
                          color="positive"
                          preserveSpace
                          size="sm"
                        />
                        {ACCESS_AND_PERMISSIONS_LABELS.ADMIN_PERMISSIONS[key]}
                      </div>
                    )
                  })}
                </div>
              </div>
            </div>
          )}

          {roleVisibility.showOPS && (
            <div className="flex flex-col">
              <Label>OPS</Label>

              <div className="mt-xs">
                <div className="text-color-info font-light">Permissions</div>

                <div className="mt-xxs gap-x-sm grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
                  {Object.keys(
                    ACCESS_AND_PERMISSIONS_LABELS.OPS_PERMISSIONS,
                  ).map((key) => {
                    const checked = anp.role.ops.permissions[key]

                    return (
                      <div
                        className={checked ? '' : `text-color-neutral-faded`}
                      >
                        <Icon
                          Icon={checked ? FaCheck : undefined}
                          className="mr-xxs"
                          color="positive"
                          preserveSpace
                          size="sm"
                        />
                        {ACCESS_AND_PERMISSIONS_LABELS.OPS_PERMISSIONS[key]}
                      </div>
                    )
                  })}
                </div>
              </div>
            </div>
          )}

          {roleVisibility.showPortal && (
            <div className="flex flex-col">
              <Label>Portal</Label>

              <div className="mt-xs">
                <div className="text-color-info font-light">Access</div>

                <div className="mt-xxs gap-x-sm grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
                  {Object.keys(ACCESS_AND_PERMISSIONS_LABELS.PORTAL_ACCESS).map(
                    (key) => {
                      const checked = anp.role.portal.access[key]

                      return (
                        <div
                          className={checked ? '' : `text-color-neutral-faded`}
                        >
                          <Icon
                            Icon={checked ? FaCheck : undefined}
                            className="mr-xxs"
                            color="positive"
                            preserveSpace
                            size="sm"
                          />
                          {ACCESS_AND_PERMISSIONS_LABELS.PORTAL_ACCESS[key]}
                        </div>
                      )
                    },
                  )}
                </div>

                <div className="mt-sm text-color-info font-light">
                  Permissions
                </div>

                <div className="mt-xxs gap-x-sm grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
                  {Object.keys(
                    ACCESS_AND_PERMISSIONS_LABELS.PORTAL_PERMISSIONS,
                  ).map((key) => {
                    const checked = anp.role.portal.permissions[key]

                    return (
                      <div
                        className={checked ? '' : `text-color-neutral-faded`}
                      >
                        <Icon
                          Icon={checked ? FaCheck : undefined}
                          className="mr-xxs"
                          color="positive"
                          preserveSpace
                          size="sm"
                        />
                        {ACCESS_AND_PERMISSIONS_LABELS.PORTAL_PERMISSIONS[key]}
                      </div>
                    )
                  })}
                </div>
              </div>
            </div>
          )}
        </div>
      </If>
    </div>
  )
}

export {PermissionsIndex}
